import React, { useState, useEffect, useRef, useContext } from "react";
import Draggable from "react-draggable";
import {
    Box, Typography, List, ListItemButton, ListItemText, ListSubheader, IconButton,
    Divider, Tooltip, Slider
} from "@mui/material";
import { FaBookOpen } from "react-icons/fa";
import { convertFromRaw, EditorState } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import MathJax from "../../../../components/react-mathjax-preview";
import { GoSidebarExpand, GoSidebarCollapse } from "react-icons/go";
import ReactTooltip from 'react-tooltip';
import { BeatLoader } from "react-spinners";
import { FiMinus, FiSquare, FiCopy, FiX } from "react-icons/fi";
import { BiExpandAlt } from "react-icons/bi";
import { AppContext } from "../../../../AppContext";
import "./Explanation.css"

// 395a93 - blue // 1976d2 - blue 2 // bb0000 - red 
const COLOR = "#dfe3e5";

const defaultImageWidth = 350;

let options = {
    blockStyleFn: (block) => {
        let data = block.getData();
        if (data.size == 0) return;

        let style = {};
        if (data.get('text-align')) style = { ...style, textAlign: data.get('text-align') }
        return {
            attributes: {
                dir: 'auto'
            },
            style
        };
    },
    entityStyleFn: (entity) => {
        const entityType = entity.get('type').toLowerCase();

        if (entityType === 'image') {
            const data = entity.getData();
            let computedWidth = data.width;
            if (computedWidth === undefined)
                computedWidth = 30;

            const float = data.alignment || data.float || data.dataVal;

            let style = {}
            let className = ""

            if (float === 'center') {
                className = "custom-image-align-center"
                style = {
                    display: 'block',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                }
            } else {
                className = `custom-image-align-${float}`
                if (float) {
                    style = {
                        float: float
                    }
                }
            }

            return {
                element: 'img',
                attributes: {
                    src: data.src,
                    className: className,
                },
                style: {
                    width: computedWidth + '%',
                    ...style,
                },
            };
        }
        if (entityType === 'draft-js-video-plugin-video') {
            const data = entity.getData();
            let computedWidth = data.width;
            if (computedWidth === undefined)
                computedWidth = 30;
            return {
                element: 'iframe',
                attributes: {
                    src: data.src,
                    width: 420,
                    height: 315,
                    allowfullscreen: '',
                }
            };
        }
    },
};

const mainWindowWidth = 800;
const mainWindowHeight = 600;

const ExplanationAndTutorials = ({ quizID, subcategoryKey, explanations = [], tutorials = [], handleClose, readOnly = false }) => {
    const nodeRef = useRef(null);
    const closeButtonRef = useRef(null);
    const restoreButtonRef = useRef(null);
    const previouslyFocusedElementRef = useRef(null);
    const appContext = useContext(AppContext);

    const [zoom, setZoom] = useState(100);

    const [minimized, setMinimized] = useState(false);
    const [maximized, setMaximized] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);

    const [selectedExplanation, setSelectedExplanation] = useState(null);
    const [selectedTutorial, setSelectedTutorial] = useState(null);

    const [iframeLoading, setIframeLoading] = useState(false);
    const [windowSize, setWindowSize] = useState({ width: mainWindowWidth, height: mainWindowHeight });

    const closeDialog = () => {
        handleClose();
        window.setTimeout(() => previouslyFocusedElementRef.current?.focus(), 0);
    };

    const handleDialogKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            closeDialog();
            return;
        }

        if (event.key !== 'Tab' || !nodeRef.current) return;

        const focusableElements = Array.from(nodeRef.current.querySelectorAll(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])'
        )).filter((element) => element.offsetParent !== null);

        if (!focusableElements.length) {
            event.preventDefault();
            return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
        }
    };

    const handleResizeMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startY = e.clientY;
        const startWidth = nodeRef.current.offsetWidth;
        const startHeight = nodeRef.current.offsetHeight;

        const onMouseMove = (moveEvent) => {
            const newWidth = Math.max(400, startWidth + (moveEvent.clientX - startX));
            const newHeight = Math.max(300, startHeight + (moveEvent.clientY - startY));
            setWindowSize({ width: newWidth, height: newHeight });
        };

        const onMouseUp = () => {
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
        };

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
    };

    useEffect(() => {
        previouslyFocusedElementRef.current = document.activeElement;
        const x = (window.innerWidth - mainWindowWidth) / 2;
        const y = (window.innerHeight - mainWindowHeight) / 2;
        setPosition({ x, y });
        const focusTimer = window.setTimeout(() => closeButtonRef.current?.focus(), 0);

        return () => window.clearTimeout(focusTimer);
    }, []);

    useEffect(() => {
        if (minimized) {
            window.setTimeout(() => restoreButtonRef.current?.focus(), 0);
        }

        const x = (window.innerWidth - mainWindowWidth) / 2;
        const y = (window.innerHeight - mainWindowHeight) / 2;
        setPosition({ x, y });
    }, [minimized]);


    const convertToHTML = (exp) => {
        let retVal = "";
        try {
            if (exp.text === null || exp.text === undefined || exp.text === "") return retVal;
            const contentState = convertFromRaw(JSON.parse(exp.text));
            const editorState = EditorState.createWithContent(contentState);
            retVal = stateToHTML(editorState.getCurrentContent(), options);
            if (exp.alt_explanation_text !== undefined && exp.alt_explanation_text !== null && exp.alt_explanation_text !== "") {
                var tempDisplayExplanationText = retVal
                var altExplanationText = exp.alt_explanation_text
                altExplanationText = altExplanationText.replace(/\n/ig, ' ')
                //finding number of time image present
                var altExplanationTextCount = altExplanationText.split(';').length
                var ExplanationImgTagCount = tempDisplayExplanationText.split('<img').length - 1
                if ((altExplanationTextCount / 2) == ExplanationImgTagCount) {
                    if (ExplanationImgTagCount >= 1) {
                        var altExplanationTextArr = altExplanationText.split(';');
                        var lastPosOfImg = 0
                        for (var j = 0; j <= altExplanationTextArr.length - 1; j = j + 2) {
                            lastPosOfImg = tempDisplayExplanationText.indexOf("<img", lastPosOfImg)
                            lastPosOfImg = lastPosOfImg + 4
                            var b = "  title =\"" + altExplanationTextArr[j] + "\"  alt =\"" + altExplanationTextArr[j + 1] + "\" "
                            var output = [tempDisplayExplanationText.slice(0, lastPosOfImg), b, tempDisplayExplanationText.slice(lastPosOfImg)].join('');
                            tempDisplayExplanationText = output
                        }
                    }
                }
                retVal = tempDisplayExplanationText
            }

        }
        catch (err) {
            console.error(err.message);
    }
        return retVal;
    }

    const getExplanationKey = (e) =>
        `${e.subject_id}_${e.topic_id}_${e.category_id}_${e.subcategory_id}_${e.explanation_id}`;

    const getExplanationAnnouncement = (explanation) => {
        const content = document.createElement('div');
        content.innerHTML = convertToHTML(explanation);

        content.querySelectorAll('img').forEach((image) => {
            const title = (image.getAttribute('title') || '').trim();
            const alt = (image.getAttribute('alt') || '').trim();
            const imageText = [
                title && `Title: ${title}`,
                alt && `Alternative text: ${alt}`,
            ].filter(Boolean).join('. ');

            image.replaceWith(document.createTextNode(imageText ? ` ${imageText}. ` : ' Image. '));
        });

        const explanationText = content.textContent.replace(/\s+/g, ' ').trim();
        return `Explanation selected: ${explanation.explanation_name}. ${explanationText || 'No explanation content is available.'}`;
    };


    const handleSelectExplanation = (expl) => {
        // console.debug("expl>>??", expl)
        if (expl && appContext.onAddActivityLog) {
            let logPayload = {
                toNomenclatureID: 0, // clicked
                quizID: quizID ? quizID : 0,
                subjectID: expl.subject_id,
                topicID: expl.topic_id,
                categoryID: expl.category_id,
                subcategoryID: expl.subcategory_id,
                explanationID: expl.explanation_id,
                activityTypeID: 40, // Explanation
            };
            appContext.onAddActivityLog(logPayload)
        }
        setSelectedExplanation(expl);
        setSelectedTutorial(null);
    }

    const handleSelectTutorial = (tutorial) => {
        // console.debug("tutorial>>??", tutorial)
        let tutorialKey = tutorial.tutorial_key;
        if (tutorialKey && appContext.onAddActivityLog) {
            let keyArr = tutorialKey.split("_")
            if (keyArr && keyArr.length) {
                let subjectID = keyArr[0] ? parseInt(keyArr[0], 10) : 0
                let topicID = keyArr[1] ? parseInt(keyArr[1], 10) : 0
                let categoryID = keyArr[2] ? parseInt(keyArr[2], 10) : 0
                let subcategoryID = keyArr[3] ? parseInt(keyArr[3], 10) : 0

                let logPayload = {
                    toNomenclatureID: 0, // clicked
                    quizID: quizID ? quizID : 0,
                    tutorialID: tutorial.quiz_id,
                    subjectID,
                    topicID,
                    categoryID,
                    subcategoryID,
                    activityTypeID: 41, // Tutorial
                };
                // console.debug("logPayload>>??", logPayload)
                appContext.onAddActivityLog(logPayload)
            }
        }
        setSelectedTutorial(tutorial);
        setSelectedExplanation(null);
    }

    return (
        <>
            {/* Minimized Floating Button */}
            {minimized && (
                <Box sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 999999 }}>
                    <Tooltip title="Restore Explanation & Tutorials">
                        <IconButton
                            ref={restoreButtonRef}
                            aria-label="Restore Explanation and Tutorials"
                            onClick={() => setMinimized(false)}
                            sx={{
                                backgroundColor: "#bb0000",
                                color: 'white',
                                width: 56,
                                height: 56,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                '&:hover': { backgroundColor: '#e40606' },
                            }}
                        >
                            <FaBookOpen size={24} />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            {maximized && !minimized ? <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    zIndex: 99,
                    width: '100%',
                    height: "100%",
                    background: "black",
                    opacity: 0.5
                }}
            ></div> : null}

            {/* Main Window */}
            <Draggable
                nodeRef={nodeRef}
                handle=".window-header"
                position={maximized ? { x: 0, y: 0 } : position}
                onDrag={(e, d) => !maximized && setPosition({ x: d.x, y: d.y })}
            >
                <Box
                    ref={nodeRef}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="explanation-tutorials-title"
                    aria-describedby="explanation-tutorials-instructions"
                    onKeyDown={handleDialogKeyDown}
                    sx={{
                        position: "fixed",
                        top: maximized ? "2vh" : 0,
                        left: maximized ? "2vw" : 0,
                        width: maximized ? "95vw" : windowSize.width,
                        height: maximized ? "95vh" : windowSize.height,

                        zIndex: 999999,
                        background: "#dfe3e5",
                        boxShadow: "0 10px 20px rgba(0,0,0,0.35)",
                        borderRadius: maximized ? 2 : 2,

                        display: minimized ? "none" : "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        minWidth: 400,
                        minHeight: 300,
                    }}

                >
                    {/* HEADER */}
                    <Box
                        className="window-header"
                        sx={{
                            background: COLOR,
                            color: "black",
                            p: 1,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            cursor: "move",
                        }}
                    >
                        <Box display="flex" alignItems="center" gap={1} height={"20px"}>
                            <IconButton
                                aria-label={isSidebarExpanded ? "Collapse explanations and tutorials list" : "Expand explanations and tutorials list"}
                                onClick={() => setIsSidebarExpanded(!isSidebarExpanded)}
                            >
                                {isSidebarExpanded ? <GoSidebarExpand /> : <GoSidebarCollapse />}
                            </IconButton>
                            <Typography id="explanation-tutorials-title" fontWeight={600}>Explanation & Tutorials</Typography>
                        </Box>

                        <Typography id="explanation-tutorials-instructions" className="sr-only">
                            Use Tab to move between controls. Select an explanation or tutorial to view it. Press Escape to close this window.
                        </Typography>
                        <Box className="sr-only" aria-live="assertive" aria-atomic="true">
                            {selectedExplanation ? getExplanationAnnouncement(selectedExplanation) : ''}
                            {selectedTutorial ? `Tutorial selected: ${selectedTutorial.quiz_name}` : ''}
                        </Box>

                        <Box display="flex" gap="6px">
                            <IconButton size="small" aria-label="Minimize Explanation and Tutorials" onClick={() => setMinimized(true)}>
                                <FiMinus />
                            </IconButton>

                            <IconButton
                                size="small"
                                aria-label={maximized ? "Restore Explanation and Tutorials window" : "Maximize Explanation and Tutorials window"}
                                onClick={() => setMaximized(!maximized)}
                            >
                                {maximized ? <FiCopy style={{ transform: "scaleX(-1)" }} /> : <FiSquare />}
                            </IconButton>

                            <IconButton ref={closeButtonRef} size="small" aria-label="Close Explanation and Tutorials" onClick={closeDialog}>
                                <FiX  />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* BODY */}
                    <Box sx={{
                        flex: 1,
                        display: "flex",
                        overflow: "hidden",
                        backgroundColor: "#f1f3f4",
                    }}>

                        {/* LEFT SIDEBAR */}
                        <Box
                            sx={{
                                width: isSidebarExpanded ? 200 : 0,
                                borderRight: "1px solid #ddd",
                                display: "flex",
                                flexDirection: "column",
                                height: "100%",
                                background: '#fff',
                                flexShrink: 0,
                            }}
                        >
                            <Box sx={{ flex: 1, overflow: "auto" }}>
                                <List subheader={
                                    <ListSubheader
                                        sx={{
                                            fontWeight: 700,
                                            backgroundColor: "#eff1f2",
                                            color: '#333',
                                            lineHeight: '40px',
                                            borderBottom: "2px solid #cfd4d8"
                                        }}
                                    >
                                        EXPLANATIONS ({explanations?.length ?? 0})
                                    </ListSubheader>
                                }>
                                    {explanations && explanations.map((e, i) => {
                                        const isSelected = selectedExplanation &&
                                            getExplanationKey(e) === getExplanationKey(selectedExplanation);

                                        return (
                                            <ListItemButton
                                                key={i}
                                                aria-label={`Explanation: ${e.explanation_name}${isSelected ? ', selected' : ''}`}
                                                onClick={() => {
                                                    handleSelectExplanation(e)
                                                }}
                                                sx={{
                                                    minHeight: 35,
                                                    backgroundColor: isSelected ? "#f5f5f5" : "transparent",
                                                    borderLeft: isSelected ? `4px solid ${COLOR}` : "4px solid transparent",
                                                }}
                                            >
                                                <ListItemText primary={e.explanation_name} />
                                            </ListItemButton>
                                        );
                                    })}
                                </List>
                            </Box>

                            <Divider />

                            <Box sx={{ flex: 1, overflow: "auto" }}>
                                <List subheader={<ListSubheader sx={{
                                    fontWeight: 700,
                                    backgroundColor: "#eff1f2",
                                    color: '#333',
                                    lineHeight: '40px',
                                    borderBottom: "2px solid #cfd4d8"
                                }}>
                                    TUTORIALS ({tutorials?.length ?? 0})
                                </ListSubheader>}>
                                    {tutorials && tutorials.map((t, i) => {
                                        const isSelected = selectedTutorial?.quiz_id === t.quiz_id;
                                        return (
                                            <ListItemButton
                                                key={i}
                                                aria-label={`Tutorial: ${t.quiz_name}${isSelected ? ', selected' : ''}`}
                                                onClick={() => {
                                                    setIframeLoading(true);
                                                    handleSelectTutorial(t)
                                                }}
                                                sx={{
                                                    minHeight: 35,
                                                    backgroundColor: isSelected ? "#f5f5f5" : "transparent",
                                                    borderLeft: isSelected ? `4px solid ${COLOR}` : "4px solid transparent",
                                                }}
                                            >
                                                <ListItemText primary={t.quiz_name} />
                                            </ListItemButton>
                                        );
                                    })}
                                </List>
                            </Box>
                        </Box>

                        {/* RIGHT CONTENT */}
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: "flex-start",
                            alignItems: "center",
                            width: "100%"
                        }}>
                            {selectedExplanation ?
                                <Box
                                    sx={{
                                        width: "100%",
                                        maxWidth: '768px',
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                        px: 1,
                                        mt: 1,
                                        px: 2
                                    }}
                                >
                                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 45 }}>
                                        Zoom
                                    </Typography>
                                    <Slider
                                        aria-label="Explanation zoom"
                                        value={zoom}
                                        min={100}
                                        max={150}
                                        // step={10}
                                        onChange={(e, val) => setZoom(val)}
                                    />
                                    <Typography variant="h6" color="primary" sx={{ minWidth: 45, textAlign: 'right' }}>
                                        {zoom}%
                                    </Typography>
                                </Box>
                                : null}
                            <div style={{ flex: 1, overflow: "auto", display: "flex", justifyContent: "center", padding: "24px", width: "100%" }}>
                                <div style={{
                                    width: "100%",
                                    maxWidth: 1200 * (zoom / 100),
                                    background: "#fff",
                                    border: "1px solid #c7c7c7",
                                    padding: selectedTutorial ? 0 : "32px",
                                    height: "100%",
                                    position: 'relative',
                                    overflow: "hidden",
                                }}>


                                    {selectedTutorial && (
                                        <div style={{ position: "relative", height: "100%", width: "100%" }}>

                                            {iframeLoading && (
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        inset: 0,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        flexDirection: "column",
                                                        gap: "10px",
                                                        background: "#fff",
                                                        zIndex: 10
                                                    }}
                                                >
                                                    <BeatLoader color={COLOR} size={12} />
                                                    <div style={{ fontSize: "14px", fontWeight: 500 }}>
                                                        Loading tutorial...
                                                    </div>
                                                </div>
                                            )}

                                            <iframe
                                                title={`Tutorial: ${selectedTutorial.quiz_name}`}
                                                src={`${window.location.origin}/takequiz?quiz_id=${selectedTutorial.quiz_id}&course_id=0&section_id=0&quiz_name=${selectedTutorial.quiz_name}&quiz_type=${selectedTutorial.quiz_type}&parent_quiz_id=${quizID}${readOnly ? '&tutorialReadOnly=true' : ''}`}
                                                width="100%"
                                                height="100%"
                                                style={{ border: "none" }}
                                                onLoad={() => setIframeLoading(false)}
                                            />
                                        </div>
                                    )}


                                    {selectedExplanation && (
                                        <div
                                        role="region"
                                        aria-label={`Explanation: ${selectedExplanation.explanation_name}`}
                                        className="zoom-container"
                                        style={{
                                            transform: `scale(${zoom / 100})`,
                                            transformOrigin: "top left",
                                            transition: "transform 0.15s ease-out",
                                            width: `${(100 / zoom) * 100}%`,
                                            height: `${(100 / zoom) * 100}%`,
                                            position: 'relative',
                                            overflow: 'auto',
                                        }}>
                                            <MathJax math={convertToHTML(selectedExplanation)} />
                                        </div>
                                    )}

                                    {!selectedExplanation && !selectedTutorial && (
                                        <Box height="100%" display="flex" alignItems="center" justifyContent="center">
                                            <Box textAlign="center">
                                                <FaBookOpen aria-hidden="true" size={60} color={COLOR} />
                                                <Typography variant="h6" mt={2}>Select Explanation or Tutorial</Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    Click an item from the left panel
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}
                                </div>
                            </div>
                        </div>
                        {/* <Box sx={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
                            {selectedTutorial && (
                                <iframe
                                    src={`http://localhost:3001/takequiz?quiz_id=${selectedTutorial.quiz_id}&course_id=0&section_id=0&quiz_name=${selectedTutorial.quiz_name}&quiz_type=${selectedTutorial.quiz_type}`}
                                    width="100%"
                                    height="100%"
                                    style={{ border: "none", flex: 1 }}
                                />
                            )}

                            {selectedExplanation && (
                                <div className="p-4">
                                    <MathJax math={convertToHTML(selectedExplanation)} />
                                </div>
                            )}

                            {!selectedExplanation && !selectedTutorial && (
                                <Box height="100%" display="flex" alignItems="center" justifyContent="center">
                                    <Box textAlign="center">
                                        <FaBookOpen size={60} color={COLOR} />
                                        <Typography variant="h6" mt={2}>Select Explanation or Tutorial</Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Click an item from the left panel
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </Box> */}

                    </Box>

                    {/* Resize grip handle */}
                    {!maximized && (
                        <Box
                            onMouseDown={handleResizeMouseDown}
                            sx={{
                                position: "absolute",
                                bottom: 0,
                                right: 0,
                                color: "#666",
                                cursor: "nwse-resize",
                                lineHeight: 0,
                                padding: "4px",
                                '&:hover': { color: '#bb0000' },
                            }}
                        >
                            <BiExpandAlt size={16} />
                        </Box>
                    )}
                </Box>
            </Draggable>
        </>
    );
};

export default ExplanationAndTutorials;
